import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast, Card } from "@jsq/ds/core";
import { FormTextInput, FormSelect, SubmitButton } from "@jsq/ds/forms";
import { useProviderObjectsLazyQuery } from "~/graphql/supergraphHooks";
import {
  type ProviderObjectsQuery,
  type ProviderObjectsQueryInput,
  type ProviderJsqObjectType,
} from "~/graphql/supergraphTypes";
import { getErrorMessage } from "~/utils/errors";
import * as styles from "./styles_vanilla.css";

// Types
type ProviderObjectType = ProviderObjectsQuery["providerObjects"][0];

type FormValues = {
  providerObjectIds: string;
  objectType: ProviderJsqObjectType | "";
  objectId: string;
};

const validationSchema = Yup.object()
  .shape({
    providerObjectIds: Yup.string(),
    objectType: Yup.string(),
    objectId: Yup.string()
      .trim()
      .matches(/^[0-9]+$/, {
        message: "Object ID must contain only numeric characters",
        excludeEmptyString: true,
      }),
  })
  .test(
    "at-least-one-query-method",
    "Please provide either Provider Object IDs or both Object Type and Object ID",
    function (values) {
      const { providerObjectIds, objectType, objectId } = values;

      // Check if provider object IDs are provided
      const hasProviderObjectIds = providerObjectIds && providerObjectIds.trim().length > 0;

      // Check if both object type and ID are provided
      const hasBothObjectTypeAndId =
        objectType && objectType.trim().length > 0 && objectId && objectId.trim().length > 0;

      // At least one method should be provided
      if (!hasProviderObjectIds && !hasBothObjectTypeAndId) {
        return this.createError({
          path: "providerObjectIds",
          message:
            "Please provide either Provider Object IDs or both Object Type and Object ID",
        });
      }

      // If object type is provided, object ID must also be provided
      if (
        objectType &&
        objectType.trim().length > 0 &&
        (!objectId || objectId.trim().length === 0)
      ) {
        return this.createError({
          path: "objectId",
          message: "Object ID is required when Object Type is provided",
        });
      }

      // If object ID is provided, object type must also be provided
      if (
        objectId &&
        objectId.trim().length > 0 &&
        (!objectType || objectType.trim().length === 0)
      ) {
        return this.createError({
          path: "objectType",
          message: "Object Type is required when Object ID is provided",
        });
      }

      return true;
    },
  );

const initialValues: FormValues = {
  providerObjectIds: "",
  objectType: "",
  objectId: "",
};

const objectTypeOptions = [
  { value: "", label: "Select Object Type" },
  { value: "ARENA", label: "Arena" },
  { value: "ACCOUNT", label: "Account" },
  { value: "PAYMENT", label: "Payment" },
  { value: "BANK_ACCOUNT", label: "Bank Account" },
  { value: "COMPLIANCE_PROFILE", label: "Compliance Profile" },
];

const QueryProviderObject: React.FC = () => {
  const [submittedData, setSubmittedData] = useState<ProviderObjectType[] | null>(null);

  const [fetchProviderObjects, { loading }] = useProviderObjectsLazyQuery({
    onCompleted: (data) => {
      if (data?.providerObjects) {
        setSubmittedData(data.providerObjects);
        toast.success(`Found ${data.providerObjects.length} provider object(s)`);
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const input: ProviderObjectsQueryInput = {};

      // Handle provider object IDs
      if (values.providerObjectIds.trim()) {
        const ids = values.providerObjectIds
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id.length > 0);
        if (ids.length > 0) {
          input.providerObjectIds = ids;
        }
      }

      // Handle object type and ID — objectId is passed as a string to support BigInt precision.
      if (values.objectType && values.objectId.trim()) {
        input.objectType = values.objectType;
        input.objectId = values.objectId.trim();
      }

      await fetchProviderObjects({
        variables: { input },
      });
    } catch {
      toast.error("Invalid input. Please check your values and try again.");
    }
  };

  return (
    <Flex
      data-testid="query-provider-object-root"
      flexDirection="column"
      className={styles.container}
    >
      <Flex flexDirection="column" className={`inset-lg ${styles.headerSection}`}>
        <Text variant="regular-xxxl">Query Provider Object</Text>
      </Flex>
      <div className={styles.contentArea} data-testid="query-provider-object-content">
        <Card className="stacked-lg">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <Flex flexDirection="column" className="stacked-md">
                <FormTextInput
                  name="providerObjectIds"
                  label="Provider Object IDs"
                  placeholder="Enter comma-separated provider object IDs (e.g., id1, id2, id3)"
                  helpText="Use this field to query specific provider objects by their IDs"
                />
                <Flex alignItems="center" justifyContent="center" className="stacked-sm">
                  <Text variant="regular-md" className={styles.dividerText}>
                    OR
                  </Text>
                </Flex>
                <FormSelect
                  name="objectType"
                  label="JSQ Object Type"
                  placeholder="Select object type"
                  helpText="Must be used together with Object ID"
                >
                  <FormSelect.Section>
                    {objectTypeOptions.map((option) => (
                      <FormSelect.Option key={option.value} value={option.value}>
                        {option.label}
                      </FormSelect.Option>
                    ))}
                  </FormSelect.Section>
                </FormSelect>
                <FormTextInput
                  name="objectId"
                  label="JSQ Object ID"
                  placeholder="Enter JSQ object ID (e.g. 1234567890)"
                  helpText="Must be used together with Object Type"
                />
              </Flex>
              <Flex justifyContent="flex-start" className="stacked-lg">
                <SubmitButton disabled={loading}>
                  {loading ? "Querying..." : "Query Provider Objects"}
                </SubmitButton>
              </Flex>
            </Form>
          </Formik>
        </Card>
        {submittedData && submittedData.length > 0 && (
          <Card>
            <Text variant="regular-xl" marginBottom="lg">
              Query Results ({submittedData.length} object
              {submittedData.length !== 1 ? "s" : ""})
            </Text>
            <Flex flexDirection="column" className="stacked-md">
              {submittedData.map((providerObject) => (
                <Card key={providerObject.id} className={styles.resultCard}>
                  <Flex flexDirection="column" className={`stacked-md ${styles.cardHeader}`}>
                    <Text variant="regular-lg" className={styles.cardTitle}>
                      Provider Object #{providerObject.id}
                    </Text>
                    <Text variant="regular-sm" className={styles.cardSubtitle}>
                      {providerObject.providerObjectType} • {providerObject.objectType}
                    </Text>
                  </Flex>
                  <Flex flexDirection="column" className="stacked-sm">
                    <div className={styles.cardRow}>
                      <Text variant="regular-sm" className={styles.cardLabel}>
                        Provider Object Type:
                      </Text>
                      <Text variant="regular-sm" className={styles.cardValue}>
                        {providerObject.providerObjectType}
                      </Text>
                    </div>
                    <div className={styles.cardRow}>
                      <Text variant="regular-sm" className={styles.cardLabel}>
                        JSQ Object Type:
                      </Text>
                      <Text variant="regular-sm" className={styles.cardValue}>
                        {providerObject.objectType}
                      </Text>
                    </div>
                    <div className={styles.cardRow}>
                      <Text variant="regular-sm" className={styles.cardLabel}>
                        Provider Object ID:
                      </Text>
                      <Text variant="regular-sm" className={styles.cardValue}>
                        {providerObject.providerObjectId}
                      </Text>
                    </div>
                    <div className={styles.cardRow}>
                      <Text variant="regular-sm" className={styles.cardLabel}>
                        JSQ Object ID:
                      </Text>
                      <Text variant="regular-sm" className={styles.cardValue}>
                        {providerObject.objectId}
                      </Text>
                    </div>
                    <div className={styles.cardRow}>
                      <Text variant="regular-sm" className={styles.cardLabel}>
                        Arena ID:
                      </Text>
                      <Text variant="regular-sm" className={styles.cardValue}>
                        {providerObject.arenaId}
                      </Text>
                    </div>
                    <div className={styles.cardRow}>
                      <Text variant="regular-sm" className={styles.cardLabel}>
                        Provider ID:
                      </Text>
                      <Text variant="regular-sm" className={styles.cardValue}>
                        {providerObject.providerId}
                      </Text>
                    </div>
                    {providerObject.refProviderObjectId && (
                      <div className={styles.cardRow}>
                        <Text variant="regular-sm" className={styles.cardLabel}>
                          Reference Object ID:
                        </Text>
                        <Text variant="regular-sm" className={styles.cardValue}>
                          {providerObject.refProviderObjectId} (
                          {providerObject.refProviderObjectType})
                        </Text>
                      </div>
                    )}
                    <div className={styles.cardRow}>
                      <Text variant="regular-sm" className={styles.cardLabel}>
                        Timestamp:
                      </Text>
                      <Text variant="regular-sm" className={styles.cardValue}>
                        {new Date(providerObject.timestamp).toLocaleString()}
                      </Text>
                    </div>
                    <Flex flexDirection="column" className={`stacked-md ${styles.dataSection}`}>
                      <Text variant="regular-sm" className={styles.dataLabel}>
                        Data:
                      </Text>
                      <pre className={styles.dataContent}>
                        {JSON.stringify(JSON.parse(providerObject.data), null, 2)}
                      </pre>
                    </Flex>
                  </Flex>
                </Card>
              ))}
            </Flex>
          </Card>
        )}
        {submittedData && submittedData.length === 0 && (
          <Card className={styles.noResultsSection}>
            <Text variant="regular-lg" marginBottom="sm">
              No Results Found
            </Text>
            <Text variant="regular-md" className={styles.noResultsDescription}>
              No provider objects were found matching your query criteria.
            </Text>
          </Card>
        )}
      </div>
    </Flex>
  );
};

export default QueryProviderObject;
export { QueryProviderObject };
